import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Stack, Typography } from '@mui/material';

import { NotFound } from 'components/NotFound/NotFound';
import { Loader } from 'components/Loader';
import { useAuth } from 'lib/auth';
import { isoToDateString } from 'utils/dateFormatter';
import { APPLICATION_STATUS } from '../../utils/applicationStatus';
import { getApplicationLinks } from '../../utils/getApplicationLinks';

const COLUMNS = ['new', 'shortlisted', 'rejected', 'accepted'];

const ApplicationKanbanCard = ({ application }) => {
  const { account, user } = useAuth();
  const navigate = useNavigate();
  const { first_name, last_name, company_name, price_per_hour, created_on } = application;

  const openDetails = () => navigate(getApplicationLinks(application, { account, user }).details);

  return (
    <Paper
      role="button"
      tabIndex={0}
      onClick={openDetails}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') openDetails();
      }}
      sx={{ p: 1.5, cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
    >
      <Stack gap={0.5}>
        <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
          {`${first_name} ${last_name}`}
        </Typography>
        <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
          {company_name || '-'}
        </Typography>
        <Stack direction="row" justifyContent="space-between" gap={1}>
          <Typography variant="caption" fontWeight={600}>
            {`${price_per_hour} SEK/h`}
          </Typography>
          <Typography variant="caption">{isoToDateString(created_on, true)}</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

export const ApplicationsKanban = ({ applications, total, isFetching, isSearching }) => {
  const columns = useMemo(() => {
    const grouped = Object.fromEntries(COLUMNS.map((status) => [status, []]));
    applications.forEach((application) => {
      const status = grouped[application.status] ? application.status : 'new';
      grouped[status].push(application);
    });
    return grouped;
  }, [applications]);

  if (isFetching && applications.length === 0) return <Loader />;

  if (applications.length === 0) {
    return (
      <NotFound
        title="No applications found"
        caption={isSearching ? 'No applications match your search.' : "This ad hasn't received any applications yet."}
        wrapperSx={{ mt: 19 }}
      />
    );
  }

  return (
    <Stack gap={1}>
      {total > applications.length && (
        <Typography variant="caption">
          {`Showing ${applications.length} of ${total} applications. Use the List view to see all.`}
        </Typography>
      )}
      <Stack direction="row" spacing={2} width="100%" boxSizing="border-box">
        {COLUMNS.map((status) => (
          <Paper
            key={status}
            sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, bgcolor: '#fdfdfd', p: 1 }}
          >
            <Typography
              variant="h6"
              sx={{ p: 1, textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
            >
              {`${APPLICATION_STATUS[status].label.toUpperCase()} (${columns[status].length})`}
            </Typography>
            <Stack spacing={1} mt={2}>
              {columns[status].map((application) => (
                <ApplicationKanbanCard key={application.application_id} application={application} />
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
};
